import { useEffect, useState } from 'react'
import { Button, Input, InputNumber, Select, Tag } from 'antd'
import * as uiApi from '@/api/uiAutomation'
import { useProjectStore } from '@/stores/project'
import type { UIElement, UIPublicModule, UIStepItem, UIStepWaitUntil } from '@/types'

const { TextArea } = Input

const WAIT_UNTIL_OPTIONS: { value: UIStepWaitUntil; label: string }[] = [
  { value: 'load', label: 'load（页面加载完成）' },
  { value: 'domcontentloaded', label: 'domcontentloaded（DOM 就绪）' },
  { value: 'networkidle', label: 'networkidle（网络空闲）' },
  { value: 'commit', label: 'commit（收到响应）' },
]

/** 步骤模式: ai=AI 翻译 / element=元素库步骤 / script=固定脚本 / email=邮箱验证码 / module=公共模块引用 */
type StepMode = 'ai' | 'element' | 'script' | 'email' | 'module'

const MODE_OPTIONS: { value: StepMode; label: string }[] = [
  { value: 'ai', label: 'AI 步骤' },
  { value: 'element', label: '元素步骤' },
  { value: 'script', label: '脚本步骤' },
  { value: 'email', label: '邮箱验证码' },
  { value: 'module', label: '公共模块' },
]

const ELEMENT_ACTION_OPTIONS = [
  { value: 'click', label: '点击' },
  { value: 'input', label: '输入' },
  { value: 'hover', label: '悬停' },
]

const ACTION_LABEL: Record<string, string> = { click: '点击', input: '输入', hover: '悬停' }

function stepMode(step: UIStepItem): StepMode {
  // 四期 H：公共模块引用步骤（module_id 非空即为模块步骤）
  if (step.module_id != null) return 'module'
  if (step.is_script) return 'script'
  // 三期：邮箱验证码步骤（IMAP 读取最新邮件提取验证码注入 {{email.code}}）
  if (step.email_code) return 'email'
  // action 字段是元素步骤的标记（AI 步骤 action 恒为 null）
  if (step.element_id != null || step.action) return 'element'
  return 'ai'
}

interface StepEditorProps {
  steps: UIStepItem[]
  onChange: (steps: UIStepItem[]) => void
  /** 元素列表刷新信号：录制导入沉淀新元素后父组件 +1，触发重新拉取元素列表 */
  refreshKey?: number
}

/**
 * UI 步骤编辑器
 * - AI 步骤：自然语言描述 + 可选输入值 + 等待策略，由 AI 翻译为 Playwright 动作（元素库作为提示注入）
 * - 元素步骤：从元素库选择元素 + 动作（点击/输入/悬停），执行时直接用定位器操作，不调 AI
 * - 脚本步骤：直接执行 Playwright 脚本（await page 可用），跳过 AI 翻译
 * - 支持上下移动与删除，顺序即执行顺序
 */
export default function StepEditor({ steps, onChange, refreshKey = 0 }: StepEditorProps) {
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0
  const [elements, setElements] = useState<UIElement[]>([])
  const [modules, setModules] = useState<UIPublicModule[]>([])

  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    Promise.all([
      uiApi.listElements({ project_id: projectId, page: 1, page_size: 100 }),
      uiApi.listPublicModules(projectId).catch(() => []),
    ])
      .then(([elRes, modRes]) => {
        if (cancelled) return
        setElements(elRes.data)
        setModules(modRes)
      })
      .catch((err) => {
        console.error('[StepEditor] 元素库/公共模块加载失败:', err)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, refreshKey])

  const elementOptions = elements.map((el) => ({
    value: el.id,
    label: `${el.name}（${el.selector_type}）`,
    title: `${el.selector_type}: ${el.selector}`,
  }))

  const moduleOptions = modules.map((m) => ({
    value: m.id,
    label: `${m.module_name || m.name}（${m.step_count} 步）`,
    title: m.module_name || m.name,
  }))

  const update = (index: number, patch: Partial<UIStepItem>) => {
    onChange(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  }

  /** 切换步骤模式时清理与该模式无关的字段 */
  const setMode = (index: number, mode: StepMode) => {
    if (mode === 'script') {
      update(index, { is_script: true, email_code: false, element_id: null, action: null, module_id: null })
    } else if (mode === 'element') {
      update(index, { is_script: false, email_code: false, element_id: undefined, action: 'click', script: '', module_id: null })
    } else if (mode === 'email') {
      update(index, { is_script: false, email_code: true, element_id: null, action: null, script: '', module_id: null })
    } else if (mode === 'module') {
      // module_id 用 0 作哨兵：0=已选「公共模块」类型但未选具体模块（deriveStepMode 据此保持 module 形态；
      // 保存校验会拦截 0 提示选择模块；undefined 会让派生模式错误回落为 AI 步骤）
      update(index, { is_script: false, email_code: false, element_id: null, action: null, script: '', module_id: 0 })
    } else {
      update(index, { is_script: false, email_code: false, element_id: null, action: null, module_id: null })
    }
  }

  /** 选择元素后自动生成步骤描述（元素步骤跳过 AI，描述仅用于列表与报告展示） */
  const setElement = (index: number, elementId: number) => {
    const el = elements.find((e) => e.id === elementId)
    const action = steps[index].action || 'click'
    update(index, {
      element_id: elementId,
      action,
      description: steps[index].description || `${ACTION_LABEL[action]} ${el?.name || ''}`,
    })
  }

  const move = (index: number, offset: -1 | 1) => {
    const target = index + offset
    if (target < 0 || target >= steps.length) return
    const next = [...steps]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const remove = (index: number) => {
    onChange(steps.filter((_, i) => i !== index))
  }

  const add = () => {
    onChange([...steps, { description: '', data: { value: '' }, is_script: false, script: '', wait_until: 'load', post_delay: 2 }])
  }

  return (
    <div>
      {steps.map((step, index) => {
        const mode = stepMode(step)
        return (
          <div
            key={index}
            style={{
              border: '1px solid var(--au-border)',
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 10,
              background: 'var(--au-surface)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'var(--au-primary-100)',
                    color: 'var(--au-primary)',
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {index + 1}
                </span>
                {mode === 'script' ? <Tag color="purple">脚本步骤</Tag> : mode === 'element' ? <Tag color="cyan">元素步骤</Tag> : mode === 'email' ? <Tag color="green">邮箱验证码</Tag> : mode === 'module' ? <Tag color="geekblue">公共模块</Tag> : <Tag color="blue">AI 步骤</Tag>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Select
                  size="small"
                  value={mode}
                  options={MODE_OPTIONS}
                  onChange={(v) => setMode(index, v)}
                  style={{ width: 104 }}
                />
                <Button type="text" size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                  上移
                </Button>
                <Button type="text" size="small" disabled={index === steps.length - 1} onClick={() => move(index, 1)}>
                  下移
                </Button>
                <Button type="text" size="small" danger onClick={() => remove(index)}>
                  删除
                </Button>
              </div>
            </div>

            {mode === 'module' ? (
              <>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={modules.length ? '选择公共模块' : '暂无公共模块（可在用例列表中开放模块）'}
                    value={step.module_id || undefined}
                    options={moduleOptions}
                    onChange={(v) => {
                      const mod = modules.find((m) => m.id === v)
                      update(index, { module_id: v, description: steps[index].description || `执行公共模块「${mod?.module_name || mod?.name || ''}」` })
                    }}
                    style={{ flex: 1, minWidth: 0 }}
                    notFoundContent="未找到公共模块"
                  />
                  <InputNumber
                    size="middle"
                    min={0}
                    max={60}
                    step={0.5}
                    precision={1}
                    value={step.post_delay ?? 2}
                    onChange={(v) => update(index, { post_delay: v == null ? 2 : Number(v) })}
                    addonAfter="s"
                    style={{ width: 130, flexShrink: 0 }}
                  />
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--au-slate-muted)', lineHeight: '20px' }}>
                  执行时后端会把该模块内步骤原位展开（支持嵌套 3 层，模块自身 post_delay 生效于最后一步）。
                  公共模块在用例列表「开放为模块」中维护。
                </div>
              </>
            ) : mode === 'script' ? (
              <TextArea
                rows={4}
                placeholder={"await page.goto('/login')\nawait page.fill('#username', 'admin')"}
                value={step.script}
                onChange={(e) => update(index, { script: e.target.value })}
                style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}
              />
            ) : mode === 'email' ? (
              <>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Input
                    placeholder="步骤描述（选填），如：获取登录验证码"
                    value={step.description}
                    onChange={(e) => update(index, { description: e.target.value })}
                    style={{ flex: 1 }}
                  />
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--au-slate-muted)', lineHeight: '20px' }}>
                  执行时通过 IMAP 读取测试邮箱（最近 30 分钟）最新邮件并提取验证码，注入变量
                  {'{{email.code}}'}；后续 AI/元素步骤的输入值中填写 {'{{email.code}}'} 即可自动替换。
                  需先在「系统配置 → UI模块设置」中配置 IMAP 服务器与账号。
                </div>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--au-slate-muted)', flexShrink: 0 }}>执行后延迟(s)</span>
                  <InputNumber
                    size="small"
                    min={0}
                    max={60}
                    step={0.5}
                    precision={1}
                    value={step.post_delay ?? 2}
                    onChange={(v) => update(index, { post_delay: v == null ? 2 : Number(v) })}
                    style={{ width: 96 }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>
                    邮件延迟送达时可增大该延迟（0~60 秒，默认 2）
                  </span>
                </div>
              </>
            ) : (
              <>
                {mode === 'element' ? (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder={elements.length ? '选择元素库元素' : '请先在元素库新建元素'}
                      value={step.element_id ?? undefined}
                      options={elementOptions}
                      onChange={(v) => setElement(index, v)}
                      style={{ flex: 1, minWidth: 0 }}
                      notFoundContent="未找到元素"
                    />
                    <Select
                      value={step.action || 'click'}
                      options={ELEMENT_ACTION_OPTIONS}
                      onChange={(v) => update(index, { action: v })}
                      style={{ width: 96, flexShrink: 0 }}
                    />
                    {step.action === 'input' ? (
                      <Input
                        placeholder="输入内容"
                        value={step.data?.value || ''}
                        onChange={(e) => update(index, { data: { value: e.target.value } })}
                        style={{ width: 180, flexShrink: 0 }}
                      />
                    ) : null}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Input
                      placeholder="步骤描述，如：在搜索框输入关键词并点击搜索按钮"
                      value={step.description}
                      onChange={(e) => update(index, { description: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <Input
                      placeholder="输入值（选填）"
                      value={step.data?.value || ''}
                      onChange={(e) => update(index, { data: { value: e.target.value } })}
                      style={{ width: 180, flexShrink: 0 }}
                    />
                    <Select
                      value={step.wait_until || 'load'}
                      options={WAIT_UNTIL_OPTIONS}
                      onChange={(v) => update(index, { wait_until: v })}
                      style={{ width: 180, flexShrink: 0 }}
                    />
                  </div>
                )}
                {/* 执行后延迟：执行完该步骤后等待的时间，便于观看每步操作（数字转 Number 后保存） */}
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--au-slate-muted)', flexShrink: 0 }}>执行后延迟(s)</span>
                  <InputNumber
                    size="small"
                    min={0}
                    max={60}
                    step={0.5}
                    precision={1}
                    value={step.post_delay ?? 2}
                    onChange={(v) => update(index, { post_delay: v == null ? 2 : Number(v) })}
                    style={{ width: 96 }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>
                    执行完该步骤后等待的时间（0~60 秒，默认 2）
                  </span>
                </div>
              </>
            )}
          </div>
        )
      })}

      <Button variant="dashed" block onClick={add} style={{ height: 38 }}>
        添加步骤
      </Button>
    </div>
  )
}
